/**
 * useHomeType — état de type de la page d'accueil voyageur
 * (spec-refonte-home-voyageur-switcher-type).
 *
 * Le type sélectionné (Tout / Chambre d'hôtes / Gîte) vit dans le query param
 * `?type=cdh|gite` (absent = « all »). Lu dès le setup pour un premier rendu SSR
 * déjà typé, partageable et indexable. `setType` fait un `router.replace` pour ne
 * pas polluer l'historique.
 *
 * L'option « Gîte » est TOUJOURS proposée, indépendamment du stock réel de gîtes
 * (décision produit : aligner la home sur la barre de recherche /search, qui
 * affiche les 3 types en permanence pour mettre en avant l'offre à venir). Un
 * `?type=gite` sans stock rend proprement un état vide (annuaire, résultats).
 *
 * Doctrine couleur (handoff design_HomewGite) :
 * - CDH  → sauge  (chrome autorisé)
 * - Gîte → ocre   (SIGNAL de type uniquement — jamais sur le chrome hors mode gîte)
 * - Tout → encre neutre
 * La couleur ne va jamais sans le mot : `label` fournit toujours le texte.
 */

export type HomeType = 'all' | 'cdh' | 'gite'

const VALID_TYPES: readonly HomeType[] = ['all', 'cdh', 'gite']

function normalizeType(raw: unknown): HomeType {
  const first = Array.isArray(raw) ? raw[0] : raw
  // Tolérant à la casse : ?type=CDH / ?type=Gite sont acceptés (partageabilité).
  const value = typeof first === 'string' ? first.toLowerCase() : first
  if (value === 'cdh') return 'cdh'
  if (value === 'gite') return 'gite'
  return 'all'
}

export function useHomeType() {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()

  const type = computed<HomeType>(() =>
    normalizeType(route.query.type),
  )

  const isAll = computed(() => type.value === 'all')
  const isCdh = computed(() => type.value === 'cdh')
  const isGite = computed(() => type.value === 'gite')

  /**
   * Renvoie la navigation en cours : un appelant qui enchaîne une autre
   * navigation (parcours de l'accueil) l'attend, sinon la seconde annulerait
   * le `replace` et `?type` ne serait jamais posé.
   */
  function setType(next: HomeType): Promise<unknown> {
    if (!VALID_TYPES.includes(next)) return Promise.resolve()

    const query = { ...route.query }
    if (next === 'all') delete query.type
    else query.type = next

    return router.replace({ query })
  }

  // Tokens de couleur dérivés — tokens SÉMANTIQUES de type (spec 4.7.0), pas les
  // accents éditoriaux bruts. En mode « Tout », teinte neutre encre.
  //   cdh  → --color-type-cdh (sauge)   | gite → --color-type-gite (ocre)
  const accentTextClass = computed(() => {
    if (isGite.value) return 'text-[var(--color-type-gite-ink)]'
    if (isCdh.value) return 'text-sage-700'
    return 'text-paper-900'
  })

  // Couleur brute du type (pour :style — bordures, fonds de bouton).
  const accentVar = computed(() => {
    if (isGite.value) return 'var(--color-type-gite)'
    if (isCdh.value) return 'var(--color-type-cdh)'
    return 'var(--color-paper-900)'
  })

  // Variante ENCRE du type — celle qui reste lisible sur un fond paper clair.
  // `accentVar` porte la couleur de marque brute : l'ocre gîte n'y plafonne qu'à
  // 2,4:1 contre paper-50, sous le minimum WCAG de 3:1 pour un composant non
  // textuel. À réserver aux traits et bordures ; `accentVar` reste le bon choix
  // pour un aplat sur lequel on écrit en clair (bouton).
  const accentInkVar = computed(() => {
    if (isGite.value) return 'var(--color-type-gite-ink)'
    if (isCdh.value) return 'var(--color-type-cdh-ink)'
    return 'var(--color-paper-900)'
  })

  const accentSoftVar = computed(() => {
    if (isGite.value) return 'var(--color-type-gite-soft)'
    if (isCdh.value) return 'var(--color-type-cdh-soft)'
    return 'var(--color-paper-100)'
  })

  // Label écrit du type actif (jamais la couleur seule).
  const typeLabel = computed(() => t(`pages.home.homeType.labels.${type.value}`))

  return {
    type,
    isAll,
    isCdh,
    isGite,
    setType,
    accentTextClass,
    accentVar,
    accentInkVar,
    accentSoftVar,
    typeLabel,
  }
}
